import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Heading from '@atlaskit/heading';
import Button from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import Spinner from '@atlaskit/spinner';
import Flag, { FlagGroup } from '@atlaskit/flag';
import InfoIcon from '@atlaskit/icon/core/information';
import SectionMessage from '@atlaskit/section-message';
import Link from '@atlaskit/link';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import SearchFailedImage from '#assets/search-failed.svg';
import { useMcpContext, MCP_POLICY_STATUS } from '../McpContextProvider';
import { useSelectedWorkspace } from '../../../Flow/ConsentContextProvider';
import { useMcpBranding } from '../mcpTerminology';
import { mcpBannerMessages, mcpPermissionsMessages } from './messages';
import McpIntentPermissionCard from './McpIntentPermissionCard';
import { MCP_PERMISSIONS_MANAGEMENT_DOC_URL as MCP_PERMISSIONS_MANAGEMENT_DOC } from '../mcpUrls';

const stackStyle = xcss({ width: '100%', maxWidth: '100%' });

const flagContainerStyle = xcss({
  position: 'fixed',
  bottom: 'space.400',
  left: 'space.400',
  // @ts-expect-error static z-index
  zIndex: 100,
  width: '400px',
  maxWidth: '90vw',
  backgroundColor: 'color.background.warning',
  borderRadius: 'radius.small',
  boxShadow: 'elevation.shadow.overlay',
});

const McpPermissions: FunctionComponent = () => {
  const { selectedWorkspace } = useSelectedWorkspace();
  const {
    isEditingPermissions,
    setIsEditingPermissions,
    mcpPolicyStatus,
    mcpByIntent,
    mcpSelectedToolsetNames,
    toggleMcpToolset: toggleToolset,
    selectedTrelloWorkspaces,
    inboxPlannerScopesDisabled,
  } = useMcpContext();

  const { formatMessage } = useIntl();
  const { product, productUnit } = useMcpBranding();
  const [flagDismissed, setFlagDismissed] = useState(false);

  useEffect(() => {
    setFlagDismissed(false);
  }, [selectedWorkspace]);

  const selectedSet = mcpSelectedToolsetNames ?? new Set<string>();

  // Don't show permissions until the user has selected a workspace
  // (either an Atlassian site or a Trello workspace).
  if (!selectedWorkspace && selectedTrelloWorkspaces.length === 0) return null;

  if (
    mcpPolicyStatus === MCP_POLICY_STATUS.loading ||
    mcpPolicyStatus === MCP_POLICY_STATUS.idle
  ) {
    return (
      <Stack xcss={stackStyle} space="space.400">
        <Heading size="small">
          <FormattedMessage {...mcpPermissionsMessages.sectionTitle} />
        </Heading>
        <Spinner size="large" label="Loading permissions" />
      </Stack>
    );
  }

  if (mcpPolicyStatus === MCP_POLICY_STATUS.error) return null;

  if (mcpByIntent.length === 0) {
    return (
      <EmptyState
        header={formatMessage(mcpPermissionsMessages.emptyStateHeading, {
          productUnit,
        })}
        description={
          <FormattedMessage
            {...mcpPermissionsMessages.emptyStateDescription}
            values={{
              howPermissionsLink: (text) => (
                <Link
                  href={MCP_PERMISSIONS_MANAGEMENT_DOC}
                  target="_blank"
                  rel="noreferrer"
                >
                  {text}
                </Link>
              ),
            }}
          />
        }
        imageUrl={SearchFailedImage}
      />
    );
  }

  const hasUnselected =
    isEditingPermissions &&
    mcpByIntent
      .flatMap((g) => g.toolsets)
      .some((t) => !selectedSet.has(t.name));

  return (
    <Stack xcss={stackStyle} space="space.200">
      <Heading size="small">
        <FormattedMessage {...mcpPermissionsMessages.sectionTitle} />
      </Heading>
      {hasUnselected && !flagDismissed && (
        <Box xcss={flagContainerStyle}>
          <FlagGroup onDismissed={() => setFlagDismissed(true)}>
            <Flag
              id="mcp-permission-warning"
              icon={
                <InfoIcon label="" color={token('color.icon.information')} />
              }
              title={formatMessage(mcpBannerMessages.editWarningTitle, {
                product,
              })}
              description={formatMessage(mcpBannerMessages.editWarningBody, {
                product,
              })}
              actions={[
                {
                  content: formatMessage(mcpBannerMessages.editWarningDismiss),
                  href: 'javascript:void(0)',
                  onClick: (e) => {
                    e.preventDefault();
                    setFlagDismissed(true);
                  },
                },
              ]}
            />
          </FlagGroup>
        </Box>
      )}
      <Stack space="space.200">
        {mcpByIntent.map((group) => (
          <McpIntentPermissionCard
            key={group.id}
            group={group}
            editMode={isEditingPermissions}
            selectedToolsetNames={selectedSet}
            onToggleToolset={(name, checked) => toggleToolset(name, checked)}
            onToggleSummaryCard={(checked) =>
              group.toolsets.forEach((t) => toggleToolset(t.name, checked))
            }
          />
        ))}
      </Stack>
      {inboxPlannerScopesDisabled && (
        <SectionMessage appearance="information">
          <Text as="p">
            <FormattedMessage
              {...mcpBannerMessages.inboxPlannerDisabledMessage}
            />
          </Text>
        </SectionMessage>
      )}
      {!isEditingPermissions && (
        <Box>
          <Button
            type="button"
            appearance="subtle"
            onClick={() => setIsEditingPermissions(true)}
          >
            <Text as="span" color="color.link">
              <FormattedMessage {...mcpPermissionsMessages.editPermissions} />
            </Text>
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export default McpPermissions;
